import type { CapModuleName } from '@vcn/ui'
import { links } from '../links'

/**
 * Sämtliche Texte der CAP-Landingpage.
 *
 * Die Inhalte sind 1:1 aus `original_assets/cap.pdf` übernommen. Ausnahme sind
 * die vier eingeklappten FAQ-Antworten – sie liegen im Entwurf nicht vor und
 * sind unten als Entwurf markiert (siehe `TODO`).
 */

export const hero = {
  title: ['Der Standard', 'für Kooperationen.'],
  lead: (
    'Mit CAP GO, FLOW und SCALE stehen drei Module für unterschiedliche ' +
    'Ausgangssituationen, Prozessanforderungen und Integrationsstufen zur Verfügung.'
  ),
}

export const openAccess = {
  eyebrow: 'Open Access mit CAP',
  title: ['Wholesale- und Wholebuy-Fähigkeit', 'strukturiert aufbauen.'],
  steps: [
    'CAP ist die Plattform für Open Access. Sie verbindet Supplier und Reseller und schafft standardisierte Zugänge, Prozesse und Integrationen für die Zusammenarbeit im Wholesale und Wholebuy.',
    'Supplier machen Netze, Footprints und Vorleistungsprodukte für Reseller nutzbar. Reseller erschließen weitere Partnernetze, Regionen und Kundenpotenziale.',
    'Mit CAP GO, FLOW und SCALE stehen drei Module für unterschiedliche Ausgangssituationen, Prozessanforderungen und Integrationsstufen zur Verfügung.',
  ],
  poles: { left: 'Reseller', right: 'Supplier' },
}

export const audiences = {
  eyebrow: 'Schnell zu den relevanten Informationen',
  items: [
    {
      id: 'reseller',
      role: 'Reseller',
      title: ['Mehr Verfügbarkeit.', 'Mehr Angebote.', 'Schneller verkaufen.'],
      body: 'Erfahren Sie, wie CAP zusätzliche Footprints erschließt, Wholebuy-Prozesse automatisiert und mehrere Supplier in Ihre Systemlandschaft integriert.',
    },
    {
      id: 'supplier',
      role: 'Supplier',
      title: ['Netze schneller auslasten.', 'Reseller einfacher aktivieren.'],
      body: 'Erfahren Sie, wie CAP Ihre Infrastruktur Wholesale-fähig macht, Reseller standardisiert anbindet und bestehende Systeme für komplexe Wholesale-Setups integriert.',
    },
  ],
}

export const modulesIntro = {
  title: 'Drei Module.',
  subtitle: 'Individueller Einstieg.',
}

export type CapModule = {
  id: string
  eyebrow: string
  /** Modulname – wählt die passende fertige Kachelgrafik aus. */
  label: CapModuleName
  title: string[]
  body: string
  bullets: string[]
  /** Auf welcher Seite die Textspalte im Desktop-Layout steht. */
  align: 'left' | 'right'
}

export const modules: CapModule[] = [
  {
    id: 'cap-go',
    eyebrow: 'CAP GO',
    label: 'GO',
    title: ['Kontrolliert in', 'Open Access', 'starten.'],
    body: 'CAP GO macht Netze, Footprints und Produkte über einen geführten Portalstart nutzbar. So können Supplier zusätzliche Reseller erreichen und Reseller weitere Märkte erschließen – ohne direkt ein eigenes Integrationsprojekt aufzusetzen.',
    bullets: ['Geführter Portalstart', 'Neue Footprints erschließen', 'Einstieg ohne Einzelprojekt'],
    align: 'left',
  },
  {
    id: 'cap-flow',
    eyebrow: 'CAP FLOW',
    label: 'FLOW',
    title: ['Wholesale- und', 'Wholebuy-Prozesse', 'verlässlich', 'automatisieren.'],
    body: 'CAP FLOW automatisiert wiederkehrende Geschäftsfälle zwischen Suppliern und Resellern. Bestellungen, Bereitstellungen, Änderungen und Entstörungen laufen über definierte Prozessschnittstellen – für einen verlässlichen Wholesale-Betrieb.',
    bullets: ['Manuelle Arbeit reduzieren', 'Rückmeldungen beschleunigen', 'Lieferqualität sichern'],
    align: 'right',
  },
  {
    id: 'cap-scale',
    eyebrow: 'CAP SCALE',
    label: 'SCALE',
    title: ['Komplexe', 'Systemwelten', 'integrieren.'],
    body: 'CAP SCALE verbindet bestehende Systeme, APIs und Datenmodelle in einer tragfähigen IT-Architektur. So lassen sich unterschiedliche Partner und Produktwelten integrieren, ohne für jeden neuen Partner ein separates IT-Projekt zu starten.',
    bullets: ['Systeme anschlussfähig machen', 'Partnerkomplexität bündeln', 'IT-Aufwand je Partner senken'],
    align: 'left',
  },
]

/** Fettgedruckte Teile innerhalb der Nutzen-Stichpunkte. */
export type Benefit = { text: string; strong?: string }

export const together = {
  eyebrow: 'CAP für Reseller und Supplier',
  title: ['CAP als gemeinsamer Standard.', 'Eigene Anbindung, gemeinsame Plattform.'],
  columns: [
    {
      role: 'Reseller',
      cta: { label: 'Direkt kontaktieren', href: links.contactReseller },
      benefits: [
        { text: 'Zusätzliche Footprints und Märkte erschließen', strong: 'Zusätzliche Footprints' },
        { text: 'Wiederkehrende Wholebuy-Prozesse automatisieren', strong: 'Wholebuy-Prozesse automatisieren' },
        { text: 'Lieferqualität sichern und Rückmeldungen an Endkunden beschleunigen', strong: 'Rückmeldungen an Endkunden beschleunigen' },
        { text: 'Mehr Supplier mit weniger IT-Aufwand integrieren', strong: 'weniger IT-Aufwand' },
      ] satisfies Benefit[],
    },
    {
      role: 'Supplier',
      cta: { label: 'Direkt kontaktieren', href: links.contactSupplier },
      benefits: [
        { text: 'Netze und Vorleistungen schneller vermarkten', strong: 'schneller vermarkten' },
        { text: 'Reseller standardisiert anbinden', strong: 'Reseller' },
        { text: 'Bestehende Systeme Wholesale-fähig machen', strong: 'Wholesale-fähig machen' },
        { text: 'Wholesale-Prozesse stabil betreiben', strong: 'Wholesale-Prozesse' },
      ] satisfies Benefit[],
    },
  ],
}

export const faq = {
  title: 'FAQ‘s',
  items: [
    {
      question: 'Wie erfolgt der Einstieg in CAP?',
      answer:
        'Der Einstieg richtet sich nach Ihrer Marktrolle, Ihrer bestehenden Systemlandschaft und dem gewünschten Automatisierungsgrad. Die Anbindung kann über einen geführten Portalzugang, eine marktübliche Standardschnittstelle wie S/PRI oder eine individuelle System- und API-Anbindung erfolgen.',
    },
    // TODO (Kunde): Die folgenden vier Antworten sind im Entwurf eingeklappt und
    // liegen nicht als Text vor. Die Formulierungen unten sind aus den Aussagen
    // der Seite abgeleitet und vor dem Livegang fachlich zu prüfen.
    {
      question: 'Muss für jeden neuen Partner eine eigene technische Anbindung umgesetzt werden?',
      answer:
        'Nein. CAP stellt standardisierte Zugänge und Prozesse bereit, die für alle angebundenen Partner gelten. Mit CAP SCALE werden bestehende Systeme, APIs und Datenmodelle einmal integriert – neue Partner werden anschließend über die gemeinsame Plattform angebunden, ohne für jeden Partner ein separates IT-Projekt zu starten.',
    },
    {
      question: 'Welche Prozesse können über CAP abgewickelt werden?',
      answer:
        'Über CAP FLOW laufen die wiederkehrenden Geschäftsfälle zwischen Suppliern und Resellern: Bestellungen, Bereitstellungen, Änderungen und Entstörungen. Sie werden über definierte Prozessschnittstellen abgewickelt, sodass Rückmeldungen zuverlässig und nachvollziehbar zurücklaufen.',
    },
    {
      question: 'Können bestehende Systeme und Schnittstellen weiter genutzt werden?',
      answer:
        'Ja. CAP SCALE verbindet bestehende Systeme, APIs und Datenmodelle in einer tragfähigen IT-Architektur. Marktübliche Standardschnittstellen wie S/PRI werden ebenso unterstützt wie individuelle System- und API-Anbindungen.',
    },
    {
      question: 'Welches CAP-Modul passt zu unseren Anforderungen?',
      answer:
        'Das hängt von Ihrer Ausgangslage ab: CAP GO für den geführten Einstieg ohne eigenes Integrationsprojekt, CAP FLOW für die Automatisierung wiederkehrender Wholesale- und Wholebuy-Prozesse und CAP SCALE für die Integration komplexer Systemwelten. Gemeinsam analysieren wir Ihre Ausgangslage und bestimmen den nächsten Schritt.',
    },
  ],
}

export const finalCta = {
  eyebrow: 'CAP',
  title: ['Welches CAP-Modul', 'passt zu Ihren Anforderungen?'],
  body: 'Gemeinsam analysieren wir Ihre Ausgangslage und bestimmen den nächsten Schritt mit CAP GO, FLOW oder SCALE.',
  cta: { label: 'Direkt kontaktieren', href: links.contactCap },
}

export const footerLinks = [
  { label: 'Carrier Hub', href: links.carrierhub },
  { label: 'Impressum', href: links.imprint },
  { label: 'Datenschutz', href: links.privacy },
]

export const ui = {
  skipToContent: 'Zum Inhalt springen',
  toAudiences: 'Zu den Informationen für Reseller und Supplier',
}
